import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useBrand } from "../lib/brand";
import { Bike, MapPin, ArrowLeft, ShieldCheck, KeyRound, Mail, FileCheck } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

// Onboarding "Email-only" : email = identité unique. Flux unifié :
// email → (compte existant avec PIN ? → login PIN) sinon → code email → PIN permanent → profil.
export default function Auth() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const location = useLocation();
  const isInscription = location.pathname === "/inscription";
  const agentCode = params.get("agent") || "";
  const role = isInscription ? "driver" : (params.get("role") || "passenger");
  const { user, login, setToken } = useAuth();
  const { name: brandName } = useBrand();

  const [step, setStep] = useState("email-entry"); // email-entry | email-otp | pin-login | set-pin
  const [email, setEmail] = useState("");
  const [emailCode, setEmailCode] = useState("");
  const [emailDemoCode, setEmailDemoCode] = useState("");
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState(null); // {token, user} awaiting PIN setup
  const [forgotMode, setForgotMode] = useState(false); // "PIN oublié" → récupération par email
  const [promo, setPromo] = useState((params.get("ref") || params.get("promo") || "").toUpperCase());
  const [conditionsAccepted, setConditionsAccepted] = useState(false);
  const [phone, setPhone] = useState("");
  const [phoneCode, setPhoneCode] = useState("");
  const [phoneDemoCode, setPhoneDemoCode] = useState("");

  const isDriver = role === "driver";
  const accent = "#D94F2B";
  const emailValid = /^\S+@\S+\.\S+$/.test(email.trim());
  const title = role === "admin" ? t("auth.adminTitle") : isDriver ? t("auth.driverTitle") : t("auth.passengerTitle");

  useEffect(() => {
    if (step === "set-pin") return; // ne pas rediriger pendant la création du PIN
    if (user && user.role) navigate(`/${user.role}`, { replace: true });
  }, [user, navigate, step]);

  useEffect(() => {
    if (params.get("kicked")) {
      sessionStorage.removeItem("bsg_kicked");
      toast.error("Vous avez été déconnecté : connexion depuis un autre appareil.");
    }
  }, [params]);

  // 1) Point d'entrée : on saisit l'email → on route vers PIN (compte connu) ou code email.
  const sendEmailCode = async () => {
    const r = await api.post("/auth/email/send-code", { email: email.trim() });
    setEmailDemoCode(r.data.demo_code || "");
    setEmailCode("");
    setStep("email-otp");
    if (r.data.demo_code) toast.success("Code email (démo) : " + r.data.demo_code);
    else toast.success("Code de vérification envoyé par email 📧");
  };

  const continueEmail = async () => {
    const isPhoneIdent = !email.includes("@") && email.replace(/[^0-9]/g, "").length >= 8;
    if (!emailValid && !isPhoneIdent) return toast.error("Entrez un email valide ou votre numéro de téléphone.");
    if (isDriver && !conditionsAccepted) return toast.error("Veuillez accepter les Conditions Partenaire.");
    setLoading(true);
    try {
      const r = await api.post("/auth/check-email", { email: email.trim() });
      setForgotMode(false);
      if (r.data.exists && r.data.has_pin) {
        setPin("");
        setStep("pin-login");
      } else if (isPhoneIdent) {
        toast.error("Aucun PIN pour ce numéro. Connectez-vous avec votre email, ou utilisez « PIN oublié ».");
      } else {
        await sendEmailCode();
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  // 2) Vérifie le code email puis connecte/crée le compte (device binding via token frais).
  const verifyAndComplete = async () => {
    if (!emailCode.trim()) return toast.error("Entrez le code reçu par email.");
    setLoading(true);
    try {
      await api.post("/auth/email/verify-code", { email: email.trim(), code: emailCode.trim() });
      const r = await api.post("/auth/email/complete", {
        email: email.trim(), role,
        ref: promo.trim() || undefined, agent: agentCode || undefined, reset: forgotMode,
      });
      if (r.data.needs_pin) {
        // Création du PIN permanent (ou reset via "PIN oublié"). On garde le token EN MÉMOIRE
        // seulement (pas de localStorage) tant que le PIN n'est pas posé, pour éviter d'entrer
        // dans l'app sans PIN si l'onglet est fermé pendant cette étape.
        setPending({ token: r.data.token, user: r.data.user });
        setPin("");
        setStep("set-pin");
        return;
      }
      login(r.data.token, r.data.user);
      toast.success("✓");
      navigate(`/${r.data.user.role}`, { replace: true });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  // Connexion Google (Emergent) : au retour, l'URL contient #session_id → échange → session JWT.
  useEffect(() => {
    // Retour OAuth Emergent : session_id arrive normalement dans le fragment (#session_id=...),
    // mais on lit AUSSI la query (?session_id=...) par robustesse sur domaine personnalisé.
    const raw = `${window.location.hash || ""}&${window.location.search || ""}`;
    const m = raw.match(/session_id=([^&]+)/);
    if (!m) return;
    const wantedRole = (typeof localStorage !== "undefined" && localStorage.getItem("bsg_google_role")) || role;
    (async () => {
      try {
        const sid = decodeURIComponent(m[1]);
        window.history.replaceState(null, "", window.location.pathname + window.location.search);
        const r = await api.post("/auth/google/session", { session_id: sid, role: wantedRole });
        login(r.data.token, r.data.user);
        toast.success("Connecté avec Google ✓");
        navigate(`/${r.data.user.role}`, { replace: true });
      } catch (e) { toast.error(apiError(e)); }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const googleLogin = () => {
    try { localStorage.setItem("bsg_google_role", role); } catch { /* ignore */ }
    const redirectUrl = window.location.origin + "/auth?role=" + role;
    window.location.href = "https://auth.emergentagent.com/?redirect=" + encodeURIComponent(redirectUrl);
  };

  // 3) Connexion rapide par PIN (compte existant).
  const loginPin = async () => {
    if (pin.length !== 4) return toast.error(t("auth.pinPh"));
    setLoading(true);
    try {
      const r = await api.post("/auth/login-pin", { email: email.trim(), pin, role });
      login(r.data.token, r.data.user);
      toast.success("✓");
      navigate(`/${r.data.user.role}`, { replace: true });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  const forgotPin = async () => {
    setLoading(true);
    try {
      setForgotMode(true);
      await sendEmailCode();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  // Connexion chauffeur par téléphone + code SMS (email facultatif, auto {tel}@bentskin.go)
  const sendPhoneCode = async () => {
    if (!phone.trim()) return toast.error("Entrez votre numéro de téléphone.");
    if (isDriver && !conditionsAccepted) return toast.error("Veuillez accepter les Conditions Partenaire.");
    setLoading(true);
    try {
      const r = await api.post("/auth/phone/send-code", { phone: phone.trim(), country_code: "CM" });
      setPhoneDemoCode(r.data.demo_code || "");
      setPhoneCode("");
      setStep("phone-otp");
      if (r.data.demo_code) toast.success("Code (démo) : " + r.data.demo_code);
      else toast.success("Code envoyé par SMS 📲");
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  const verifyPhone = async () => {
    if (!phoneCode.trim()) return toast.error("Entrez le code reçu par SMS.");
    setLoading(true);
    try {
      const r = await api.post("/auth/phone/login", {
        phone: phone.trim(), country_code: "CM", code: phoneCode.trim(), role,
        ref: promo.trim() || undefined, agent: agentCode || undefined,
      });
      if (r.data.needs_pin) { setPending({ token: r.data.token, user: r.data.user }); setPin(""); setStep("set-pin"); return; }
      login(r.data.token, r.data.user);
      toast.success("✓");
      navigate(`/${r.data.user.role}`, { replace: true });
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  // 4) Enregistre le PIN permanent puis entre dans l'app (profil si nouveau).
  const setPinSubmit = async () => {
    if (pin.length !== 4 || !/^\d{4}$/.test(pin)) return toast.error(t("auth.pinPh"));
    setLoading(true);
    try {
      // Le token n'est pas encore en localStorage → on l'envoie explicitement pour cet appel.
      await api.post("/auth/set-pin", { pin }, { headers: { Authorization: `Bearer ${pending.token}` } });
      toast.success("✓");
      login(pending.token, pending.user);
      navigate(`/${pending.user.role}`, { replace: true });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  const pinInput = (val, setter, testid, onEnter) => (
    <Input
      data-testid={testid}
      value={val}
      onChange={(e) => setter(e.target.value.replace(/\D/g, "").slice(0, 4))}
      onKeyDown={(e) => { if (e.key === "Enter" && onEnter) onEnter(); }}
      inputMode="numeric"
      placeholder="••••"
      maxLength={4}
      className="mt-1.5 h-14 rounded-xl border-2 text-center text-2xl tracking-[0.6em] font-bold"
    />
  );

  const subtitle =
    step === "email-entry" ? "Entrez votre email pour continuer — un seul compte par email."
    : step === "phone-entry" ? "Entrez votre numéro — vous recevrez un code par SMS."
    : step === "phone-otp" ? "Entrez le code reçu par SMS"
    : step === "email-otp" ? (forgotMode ? "Code de récupération envoyé par email" : "Vérifiez votre email pour continuer")
    : step === "pin-login" ? email
    : t("auth.setPinSub");

  return (
    <div className="min-h-screen bg-[#FDF8F0] flex flex-col">
      <div className="max-w-md w-full mx-auto px-5 py-6 flex-1 flex flex-col">
        <button data-testid="auth-back" onClick={() => navigate("/")} className="flex items-center gap-2 text-[#75807A] hover:text-[#1A1A1A] text-sm font-medium">
          <ArrowLeft className="w-4 h-4" /> {brandName}
        </button>

        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="mt-10 bg-white rounded-3xl border border-[#E2DFD8] p-7 shadow-[0_8px_40px_rgba(0,0,0,0.05)]"
        >
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: accent }}>
            {step === "pin-login" || step === "set-pin" ? <KeyRound className="w-7 h-7 text-white" /> : isDriver ? <Bike className="w-7 h-7 text-white" /> : <MapPin className="w-7 h-7 text-white" />}
          </div>
          <h1 className="mt-5 font-head font-black text-2xl tracking-tight text-[#1A1A1A]">
            {step === "pin-login" ? t("auth.pinTitle") : step === "set-pin" ? t("auth.setPinTitle") : title}
          </h1>
          <p className="mt-1 text-[#75807A] text-sm">{subtitle}</p>
          {isInscription && agentCode && (
            <div data-testid="inscription-agent-badge" className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#F0EBE1] px-3 py-1 text-xs font-semibold text-[#1A1A1A]">
              <ShieldCheck className="w-3.5 h-3.5 text-[#D94F2B]" /> Parrainé par un agent
            </div>
          )}

          {/* ÉTAPE 1 — Email */}
          {step === "email-entry" && (
            <div className="mt-7 space-y-4" data-testid="auth-email-entry">
              <div>
                <label className="text-sm font-medium text-[#3B423E]">Email</label>
                <div className="relative mt-1.5">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#75807A]" />
                  <Input data-testid="auth-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") continueEmail(); }} placeholder="votre@email.com" className="h-14 rounded-xl border-2 pl-11" />
                </div>
                <p className="mt-1 text-xs text-[#75807A]">Votre profil et votre portefeuille sont liés à cet email.</p>
              </div>

              {isDriver && (
                <div data-testid="partner-conditions" className="rounded-2xl border-2 border-[#E2DFD8] bg-[#FDF8F0] p-4">
                  <div className="flex items-center gap-2 text-[#1A1A1A] font-head font-bold text-sm">
                    <FileCheck className="w-4 h-4 text-[#D94F2B]" /> Conditions Partenaire Mboa Go
                  </div>
                  <ul className="mt-3 space-y-2 text-xs text-[#3B423E] leading-relaxed">
                    <li className="flex gap-2"><span className="font-bold text-[#D94F2B]">1.</span> Je suis indépendant, pas salarié.</li>
                    <li className="flex gap-2"><span className="font-bold text-[#D94F2B]">2.</span> Commission de 19 % par course. Activation unique de 600 FCFA — gelée (offerte) jusqu'au 1er janvier 2027.</li>
                    <li className="flex gap-2"><span className="font-bold text-[#D94F2B]">3.</span> Je respecte le code de la route et j'ai une assurance.</li>
                    <li className="flex gap-2"><span className="font-bold text-[#D94F2B]">4.</span> Mboa Go est uniquement une plateforme de mise en relation.</li>
                  </ul>
                  <label data-testid="partner-conditions-label" className="mt-3 flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" data-testid="partner-conditions-checkbox" checked={conditionsAccepted} onChange={(e) => setConditionsAccepted(e.target.checked)} className="mt-0.5 w-5 h-5 accent-[#D94F2B] shrink-0" />
                    <span className="text-sm font-semibold text-[#1A1A1A]">J'accepte les Conditions Partenaire Mboa Go</span>
                  </label>
                </div>
              )}
              <Button data-testid="auth-send-otp" onClick={continueEmail} disabled={loading || !emailValid} className="w-full h-14 rounded-xl text-white font-bold text-lg hover:-translate-y-0.5 transition-transform" style={{ background: accent }}>
                {loading ? t("common.loading") : "Continuer avec mon email"}
              </Button>
              <button data-testid="auth-google-btn" onClick={googleLogin} className="w-full h-14 rounded-xl border-2 border-[#E2DFD8] bg-white text-[#1A1A1A] font-bold text-base inline-flex items-center justify-center gap-2 hover:border-[#D94F2B] hover:-translate-y-0.5 transition-all">
                <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5" /> Continuer avec Google
              </button>
              <button data-testid="auth-switch-phone" onClick={() => setStep("phone-entry")} className="w-full text-sm text-[#D94F2B] font-semibold">📲 Ou {isDriver ? "connectez-vous" : "inscrivez-vous"} par téléphone (SMS)</button>
            </div>
          )}

          {/* ÉTAPE — Téléphone (chauffeur) */}
          {step === "phone-entry" && (
            <div className="mt-7 space-y-4" data-testid="auth-phone-entry">
              <div>
                <label className="text-sm font-medium text-[#3B423E]">Numéro de téléphone</label>
                <Input data-testid="auth-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") sendPhoneCode(); }} placeholder="+237 6XX XXX XXX" className="mt-1.5 h-14 rounded-xl border-2" />
                <p className="mt-1 text-xs text-[#75807A]">Vous recevrez un code par SMS. L'email est facultatif — nous en créons un pour vous.</p>
              </div>
              {isDriver && (
                <label data-testid="phone-conditions-label" className="flex items-start gap-3 cursor-pointer rounded-2xl border-2 border-[#E2DFD8] bg-[#FDF8F0] p-4">
                  <input type="checkbox" data-testid="phone-conditions-checkbox" checked={conditionsAccepted} onChange={(e) => setConditionsAccepted(e.target.checked)} className="mt-0.5 w-5 h-5 accent-[#D94F2B] shrink-0" />
                  <span className="text-sm font-semibold text-[#1A1A1A]">J'accepte les Conditions Partenaire (commission 19 %, activation 600 FCFA gelée jusqu'au 01/01/2027, indépendant, assurance).</span>
                </label>
              )}
              <Button data-testid="auth-send-sms" onClick={sendPhoneCode} disabled={loading || !phone.trim()} className="w-full h-14 rounded-xl text-white font-bold text-lg" style={{ background: accent }}>
                {loading ? t("common.loading") : "Recevoir le code par SMS"}
              </Button>
              <button data-testid="auth-switch-email" onClick={() => setStep("email-entry")} className="w-full text-sm text-[#75807A] hover:text-[#1A1A1A]">Utiliser mon email plutôt</button>
            </div>
          )}

          {/* ÉTAPE — Code SMS (chauffeur) */}
          {step === "phone-otp" && (
            <div className="mt-7 space-y-4" data-testid="auth-phone-otp">
              {phoneDemoCode && (
                <div className="rounded-xl bg-[#F0EBE1] border border-[#E2DFD8] p-4 flex items-center justify-between">
                  <div><div className="text-xs text-[#75807A] uppercase tracking-wide">Code SMS (démo)</div><div className="font-head font-black text-2xl tracking-[0.3em] text-[#1A1A1A]" data-testid="phone-demo-code">{phoneDemoCode}</div></div>
                  <button data-testid="use-phone-demo-code" onClick={() => setPhoneCode(phoneDemoCode)} className="text-sm font-semibold text-[#D94F2B] underline">Utiliser</button>
                </div>
              )}
              <div>
                <label className="text-sm font-medium text-[#3B423E]">Code reçu par SMS</label>
                <Input data-testid="auth-phone-code-input" value={phoneCode} onChange={(e) => setPhoneCode(e.target.value.replace(/\D/g, "").slice(0, 6))} onKeyDown={(e) => { if (e.key === "Enter") verifyPhone(); }} inputMode="numeric" placeholder="••••••" maxLength={6} className="mt-1.5 h-14 rounded-xl border-2 text-center text-2xl tracking-[0.4em] font-bold" />
                <p className="mt-1 text-xs text-[#75807A]">Envoyé au <span className="font-semibold text-[#1A1A1A]">{phone}</span></p>
              </div>
              <Button data-testid="auth-verify-phone" onClick={verifyPhone} disabled={loading} className="w-full h-14 rounded-xl text-white font-bold text-lg" style={{ background: accent }}>
                {loading ? t("common.loading") : "Se connecter"}
              </Button>
              <div className="flex justify-between text-sm">
                <button data-testid="auth-phone-back" onClick={() => setStep("phone-entry")} className="text-[#75807A] hover:text-[#1A1A1A]">Retour</button>
                <button data-testid="auth-phone-resend" onClick={sendPhoneCode} className="text-[#D94F2B] font-medium">Renvoyer</button>
              </div>
            </div>
          )}

          {/* ÉTAPE 2 — Code email */}
          {step === "email-otp" && (
            <div className="mt-7 space-y-4" data-testid="auth-email-step">
              {emailDemoCode && (
                <div className="rounded-xl bg-[#F0EBE1] border border-[#E2DFD8] p-4 flex items-center justify-between">
                  <div>
                    <div className="text-xs text-[#75807A] uppercase tracking-wide">Code email (démo)</div>
                    <div className="font-head font-black text-2xl tracking-[0.3em] text-[#1A1A1A]" data-testid="email-demo-code">{emailDemoCode}</div>
                  </div>
                  <button data-testid="use-email-demo-code" onClick={() => setEmailCode(emailDemoCode)} className="text-sm font-semibold text-[#D94F2B] underline">Utiliser</button>
                </div>
              )}
              <div>
                <label className="text-sm font-medium text-[#3B423E]">Code reçu par email</label>
                <Input data-testid="auth-email-code-input" value={emailCode} onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, "").slice(0, 4))} onKeyDown={(e) => { if (e.key === "Enter") verifyAndComplete(); }} inputMode="numeric" placeholder="••••" maxLength={4} className="mt-1.5 h-14 rounded-xl border-2 text-center text-2xl tracking-[0.5em] font-bold" />
                <p className="mt-1 text-xs text-[#75807A]">Envoyé à <span className="font-semibold text-[#1A1A1A]">{email}</span></p>
              </div>
              <Button data-testid="auth-verify-email" onClick={verifyAndComplete} disabled={loading} className="w-full h-14 rounded-xl text-white font-bold text-lg hover:-translate-y-0.5 transition-transform" style={{ background: accent }}>
                {loading ? t("common.loading") : "Vérifier et continuer"}
              </Button>
              <div className="flex justify-between text-sm">
                <button data-testid="auth-email-back" onClick={() => setStep("email-entry")} className="text-[#75807A] hover:text-[#1A1A1A]">{t("auth.back")}</button>
                <button data-testid="auth-email-resend" onClick={sendEmailCode} className="text-[#D94F2B] font-medium">{t("auth.resend")}</button>
              </div>
            </div>
          )}

          {/* ÉTAPE — Connexion PIN (compte existant) */}
          {step === "pin-login" && (
            <div className="mt-7 space-y-4">
              <div>
                <label className="text-sm font-medium text-[#3B423E]">{t("auth.pinLabel")}</label>
                {pinInput(pin, setPin, "auth-pin-input", loginPin)}
              </div>
              <Button data-testid="auth-pin-login" onClick={loginPin} disabled={loading} className="w-full h-14 rounded-xl text-white font-bold text-lg hover:-translate-y-0.5 transition-transform" style={{ background: accent }}>
                {loading ? t("common.loading") : t("auth.pinLogin")}
              </Button>
              <div className="flex justify-between text-sm">
                <button data-testid="auth-change-email" onClick={() => setStep("email-entry")} className="text-[#75807A] hover:text-[#1A1A1A]">{t("auth.back")}</button>
                <button data-testid="auth-forgot-pin" onClick={forgotPin} className="text-[#D94F2B] font-medium">{t("auth.forgotPin")}</button>
              </div>
            </div>
          )}

          {/* ÉTAPE — Création du PIN permanent */}
          {step === "set-pin" && (
            <div className="mt-7 space-y-4">
              <div>
                <label className="text-sm font-medium text-[#3B423E]">{t("auth.pinLabel")}</label>
                {pinInput(pin, setPin, "auth-set-pin-input", setPinSubmit)}
                <p className="mt-1 text-xs text-[#75807A]">Ce code vous servira pour vos prochaines connexions rapides.</p>
              </div>
              <Button data-testid="auth-set-pin" onClick={setPinSubmit} disabled={loading} className="w-full h-14 rounded-xl text-white font-bold text-lg hover:-translate-y-0.5 transition-transform" style={{ background: accent }}>
                {loading ? t("common.loading") : t("auth.createPin")}
              </Button>
            </div>
          )}

          <div className="mt-6 flex items-center gap-2 text-xs text-[#75807A]">
            <ShieldCheck className="w-4 h-4 text-[#D94F2B]" /> {t("safety.f2t")}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
